import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { postOfferHeader, updateOfferHeader } from "../../../api/offerHeader";
import { OFFER_STATUS, ROLES } from "../../../utils/constants";
import { alertError, alertSuccess } from "../../../utils/logger";
import LoadingDialog from "./LoadingDialog";
import { useHistory } from "react-router-dom";
import { Description } from "@material-ui/icons";
import Editor from "../editor/Editor";

function getEmptyOfferHeader() {
  return {
    id: null,
    revision_num: 0,
    num_oferta: null,
    cliente_id: null,
    localizacion: null,
    referencia: null,
    estado_id: OFFER_STATUS.BORRADOR,
    valor_IVA: null,
    iva_id: null,
    descripcion: null,

    def_margen_material: 0,
    def_margen_m_obra: 0,
    def_margen_subcontrata: 0,

    def_comisiones: 0,
    def_coste_hora_m_obra: 0,
    def_trabajadores_asignados: 0,
  };
}

const EditOfferHeaderDialog = (props) => {
  const { title, open, setOpen, data, customers, iva, onSave } = props;

  const [loading, setLoading] = useState(false);
  const [currentCustomer, setCurrentCustomer] = useState({});
  const [currentIVA, setCurrentIVA] = useState({});

  const [offerHeader, setOfferHeader] = useState({
    ...getEmptyOfferHeader(),
    ...data,
  });

  const history = useHistory();

  const loggedUser = useSelector(
    (store) => store.authentication?.user,
    shallowEqual
  );
  const isOfferManager = loggedUser?.role?.rango === ROLES.GESTOR_OFERTAS;

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    setOfferHeader({
      ...getEmptyOfferHeader(),
      ...data,
    });
    return undefined;
  }, [open, data]);

  function saveOfferHeader() {
    const validateHeader = () => {
      let error = "";

      if (!offerHeader?.num_oferta) {
        error = "El núero de oferta es obligatorio.";
      } else if (!offerHeader?.cliente_id) {
        error = "El cliente es obligatorio.";
      } else if (!offerHeader?.localizacion) {
        error = "La localizacion es obligatoria.";
      }

      if (error) {
        alertError({
          error: error,
        });
        return false;
      }

      return true;
    };

    if (!validateHeader()) {
      return;
    }

    let saveOfferHeader = {
      ...offerHeader,
      referencia: offerHeader?.referencia || offerHeader?.num_oferta,
    };

    if (offerHeader.iva_id === null) {
      const iva21 = iva?.find((x) => x.valor === 0.21);
      saveOfferHeader = {
        ...saveOfferHeader,
        valor_IVA: iva21?.valor,
        iva_id: iva21?.id,
        descripcion: iva21?.descripcion,
      };
    }

    const t0 = Date.now();
    setLoading(true);

    if (!saveOfferHeader?.id) {
      postOfferHeader(saveOfferHeader, loggedUser?.accessToken)
        .then((res) => {
          if (res.status === 201) {
            alertSuccess({
              title: "¡Guardada!",
              customMessage: "Cabecera de oferta creada con éxito",
            });
            setLoading(false);
            onSave(res.data);
            setOpen(false);
            setOfferHeader(getEmptyOfferHeader());
            history.push({
              pathname: `/ofertas/${res.data?.id}/${res.data?.revision_num}/edit`,
            });
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "No se ha podido crear la oferta",
          });
          setLoading(false);
        });
    } else {
      updateOfferHeader(
        saveOfferHeader?.id,
        saveOfferHeader,
        loggedUser?.accessToken
      )
        .then((res) => {
          if (res.status === 204) {
            const secs = ((Date.now() - t0) / 1000).toFixed(1);
            setLoading(false);

            alertSuccess({
              title: "¡Guardado!",
              customMessage: `Cambios guardados con éxito (${secs}s)`,
            });

            onSave(saveOfferHeader);
            setOpen(false);
            setOfferHeader(getEmptyOfferHeader());
          }
        })
        .catch((error) => {
          const secs = ((Date.now() - t0) / 1000).toFixed(1);
          setLoading(false);

          alertError({
            error,
            customMessage: `No se han podido guardar los cambios (${secs}s)`,
          });
        });
    }
  }

  const handleChange = (element) => (event) => {
    setOfferHeader({ ...offerHeader, [element]: event.target.value });
  };

  const handleChangeEditor = (element, value) => {
    if (value?.trim() !== "") {
      setOfferHeader({ ...offerHeader, [element]: value });
    }
  };

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth="lg"
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        aria-labelledby="table-dialog"
        className="backdrop"
      >
        <DialogTitle id="table-dialog">{title}</DialogTitle>
        <DialogContent>
          <div className="row d-flex align-items-center">
            <div className="col-md-4">
              <TextField
                label="Número de oferta"
                value={offerHeader.num_oferta}
                onChange={handleChange("num_oferta")}
                InputLabelProps={{
                  shrink: true,
                }}
                placeholder="yyyyMMdd"
                margin="normal"
                variant="outlined"
                required
              />
            </div>
            <div className="col-md-4">
              <Autocomplete
                id="autocomplete-cliente"
                disablePortal
                disableClearable
                options={customers}
                getOptionLabel={(option) => option.nombre}
                value={
                  customers?.find((x) => x.id === offerHeader?.cliente_id) || ""
                }
                onChange={(event, selected) => {
                  setCurrentCustomer(selected);
                  setOfferHeader({
                    ...offerHeader,
                    cliente_id: selected?.id || null,
                    def_margen_material:
                      !offerHeader.id
                        ? selected?.margen_base_material ?? 0
                        : offerHeader.def_margen_material,
                    def_margen_m_obra:
                      !offerHeader.id
                        ? selected?.margen_base_m_obra ?? 0
                        : offerHeader.def_margen_m_obra,
                    def_margen_subcontrata:
                      !offerHeader.id
                        ? selected?.margen_base_subcontrata ?? 0
                        : offerHeader.def_margen_subcontrata,
                    def_comisiones:
                      !offerHeader.id
                        ? selected?.base_comisiones ?? 0
                        : offerHeader.def_comisiones,
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cliente"
                    margin="normal"
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    required
                  />
                )}
              />
            </div>
            <div className="col-md-4">
              <TextField
                label="Localización"
                value={offerHeader.localizacion}
                onChange={handleChange("localizacion")}
                InputLabelProps={{
                  shrink: true,
                }}
                margin="normal"
                variant="outlined"
                required
              />
            </div>
          </div>
          <div className="row d-flex align-items-center">
            <div className="col-md-4">
              <TextField
                id={`reference`}
                label="Referencia de obra"
                value={offerHeader.referencia}
                onChange={handleChange("referencia")}
                InputLabelProps={{
                  shrink: true,
                }}
                margin="normal"
                variant="outlined"
                required
              />
            </div>
            <div className="col-md-4">
              <Autocomplete
                id="autocomplete-iva"
                disablePortal
                disableClearable
                options={iva}
                getOptionLabel={(option) => option.descripcion}
                value={
                  iva?.find((x) => x.id === offerHeader?.iva_id) ||
                  iva?.find((x) => x.valor === 0.21)
                }
                onChange={(event, selected) => {
                  setOfferHeader({
                    ...offerHeader,
                    valor_IVA: selected?.valor || null,
                    iva_id: selected?.id || null,
                    descripcion: selected?.descripcion || null,
                  });
                  setCurrentIVA(selected);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="IVA"
                    margin="normal"
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    required
                  />
                )}
              />
            </div>
            {!isOfferManager && <div className="col-md-4">
              <TextField
                label="Coste/hora (€) Subcontrata"
                value={offerHeader.def_coste_hora_subcontrata}
                onChange={(event) => {
                  setOfferHeader({
                    ...offerHeader,
                    def_coste_hora_subcontrata: parseFloat(event.target.value),
                  });
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  inputProps: {
                    min: 0,
                  },
                }}
                type="number"
                margin="normal"
                variant="outlined"
                required
              />
            </div>}
          </div>
			{!isOfferManager && <>
          <hr />
          <div className="row d-flex align-items-center">
            <div className="col-md-3">
              <strong>Margen</strong>
            </div>
            <div className="col-md-3">
              <TextField
                label="Material (%)"
                value={
                  offerHeader.def_margen_material ??
                  currentCustomer.margen_base_material
                }
                onChange={(event) => {
                  setOfferHeader({
                    ...offerHeader,
                    def_margen_material: parseFloat(event.target.value),
                  });
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  inputProps: {
                    min: 0,
                  },
                }}
                type="number"
                margin="normal"
                variant="outlined"
                required
              />
            </div>
            <div className="col-md-3">
              <TextField
                label="Mano de obra (%)"
                value={
                  offerHeader.def_margen_m_obra ??
                  currentCustomer.margen_base_m_obra
                }
                onChange={(event) => {
                  setOfferHeader({
                    ...offerHeader,
                    def_margen_m_obra: parseFloat(event.target.value),
                  });
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  inputProps: {
                    min: 0,
                  },
                }}
                type="number"
                margin="normal"
                variant="outlined"
                required
              />
            </div>
            <div className="col-md-3">
              <TextField
                label="Subcontrata (%)"
                value={
                  offerHeader.def_margen_subcontrata ??
                  currentCustomer.margen_base_subcontrata
                }
                onChange={(event) => {
                  setOfferHeader({
                    ...offerHeader,
                    def_margen_subcontrata: parseFloat(event.target.value),
                  });
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  inputProps: {
                    min: 0,
                  },
                }}
                type="number"
                margin="normal"
                variant="outlined"
                required
              />
            </div>
          </div>
          <hr />
			</>}
          <div className="row d-flex align-items-center">
            <div className="col-md-3">
              <strong>Recursos internos</strong>
            </div>
            <div className="col-md-3">
              <TextField
                label="Trabajadores"
                value={offerHeader.def_trabajadores_asignados}
                onChange={(event) => {
                  setOfferHeader({
                    ...offerHeader,
                    def_trabajadores_asignados: parseFloat(event.target.value),
                  });
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  inputProps: {
                    min: 0,
                  },
                }}
                type="number"
                margin="normal"
                variant="outlined"
                required
              />
            </div>
            {!isOfferManager && <div className="col-md-3">
              <TextField
                label="Coste/hora (€)"
                value={offerHeader.def_coste_hora_m_obra}
                onChange={(event) => {
                  setOfferHeader({
                    ...offerHeader,
                    def_coste_hora_m_obra: parseFloat(event.target.value),
                  });
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  inputProps: {
                    min: 0,
                  },
                }}
                type="number"
                margin="normal"
                variant="outlined"
                required
              />
            </div>}
            {!isOfferManager && <div className="col-md-3">
              <TextField
                label="Comisiones (%)"
                value={
                  offerHeader.def_comisiones ?? currentCustomer.base_comisiones
                }
                onChange={(event) => {
                  setOfferHeader({
                    ...offerHeader,
                    def_comisiones: parseFloat(event.target.value),
                  });
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  inputProps: {
                    min: 0,
                  },
                }}
                type="number"
                margin="normal"
                variant="outlined"
                required
              />
            </div>}
          </div>
          <hr />
          <div>Cabecera *</div>
          <br />
          <Editor
            body={offerHeader?.cabecera_descripcion || ""}
            setBody={(new_body) =>
              handleChangeEditor("cabecera_descripcion", new_body)
            }
            className="max-height"
            placeholder="Introduce la cabecera aquí..."
          />
          <hr />
          <div>
            <div>Condiciones de venta *</div>
            <br />
            <Editor
              body={offerHeader?.pie_condiciones_venta || ""}
              setBody={(new_body) =>
                handleChangeEditor("pie_condiciones_venta", new_body)
              }
              className="max-height"
              placeholder="Introduce las condiciones de venta aquí..."
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            className="mr-2"
            onClick={() => {
              setOpen(false);
              setOfferHeader(getEmptyOfferHeader());
            }}
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              saveOfferHeader();
            }}
            variant="outlined"
            color="primary"
          >
            Guardar
          </Button>
          <br />
        </DialogActions>
      </Dialog>
      <LoadingDialog open={loading} title="Recalculando oferta..." />

    </>
  );
};
export default EditOfferHeaderDialog;
